import { useState, useEffect, useRef } from "react"
import { Search, ChevronDown, Check } from "lucide-react"

const API_URL = "https://api.centralapis.com/api/v1/country"

// Fonction helper pour faire des requêtes à l'API externe sans credentials
const fetchCountries = async (url) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Ne pas inclure credentials pour éviter les problèmes CORS
      credentials: 'omit',
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching countries:", error)
    throw error
  }
}

export default function PhoneInput({
  value = "",
  onChange,
  onCountryChange,
  defaultCountry = "CI", // Côte d'Ivoire par défaut
  placeholder = "",
  className = "",
  disabled = false,
  error = false,
  name = "phone",
  required = false
}) {
  const [countries, setCountries] = useState([])
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredCountries, setFilteredCountries] = useState([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)

  // Charger les pays au montage
  useEffect(() => {
    loadCountries()
  }, [])

  // Définir le pays par défaut
  useEffect(() => {
    if (countries.length > 0 && defaultCountry && !selectedCountry) {
      const country = countries.find(c => c.alpha2Code === defaultCountry)
      if (country) {
        setSelectedCountry(country)
        if (onCountryChange) {
          onCountryChange(country.alpha2Code)
        }
      }
    }
  }, [countries, defaultCountry])

  // Filtrer les pays selon le terme de recherche
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCountries(countries)
    } else {
      const filtered = countries.filter(country => 
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.alpha2Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.callingCodes?.some(code => code.includes(searchTerm))
      )
      setFilteredCountries(filtered)
    }
  }, [searchTerm, countries])

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
        setSearchTerm("")
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isDropdownOpen])

  const loadCountries = async () => {
    setLoading(true)
    try {
      const data = await fetchCountries(API_URL)
      
      if (data?.etat && data?.result) {
        const sorted = data.result.sort((a, b) => 
          a.name.localeCompare(b.name)
        )
        setCountries(sorted)
        setFilteredCountries(sorted)
      }
    } catch (error) {
      console.error("Error loading countries:", error)
      setCountries([])
      setFilteredCountries([])
    } finally {
      setLoading(false)
    }
  }

  const handleCountrySelect = (country) => {
    setSelectedCountry(country)
    setIsDropdownOpen(false)
    setSearchTerm("")
    if (onCountryChange) {
      onCountryChange(country.alpha2Code)
    }
  }

  const handlePhoneChange = (e) => {
    // Ne garder que les chiffres
    const phoneValue = e.target.value.replace(/\D/g, "")
    onChange(phoneValue)
  }

  const callingCode = selectedCountry?.callingCodes?.[0] || ""
  const displayValue = value ? value.replace(/\D/g, "") : ""

  return (
    <div className={`relative ${className}`}>
      <div className="flex">
        {/* Sélecteur de pays avec drapeau */}
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            className={`flex items-center gap-1.5 px-3 py-2.5 border-2 rounded-l-lg bg-white text-sm font-medium focus:outline-none transition-all duration-300 ${
              error
                ? "border-red-300 focus:ring-2 focus:ring-red-500"
                : "border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a]"
            } ${
              disabled ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          >
            {selectedCountry ? (
              <>
                <img
                  src={selectedCountry.flag}
                  alt={selectedCountry.name}
                  className="w-5 h-4 flex-shrink-0 rounded-sm object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <span className="text-gray-700">+{callingCode}</span>
              </>
            ) : (
              <span className="text-gray-500">+XX</span>
            )}
            <ChevronDown 
              className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                isDropdownOpen ? "transform rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown des pays */}
          {isDropdownOpen && (
            <div className="absolute z-50 w-72 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
              {/* Barre de recherche */}
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un pays..."
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a]"
                  />
                </div>
              </div>

              {/* Liste des pays */}
              <div className="overflow-y-auto max-h-64">
                {loading ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Chargement...
                  </div>
                ) : filteredCountries.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Aucun pays trouvé
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredCountries.map((country) => {
                      const isSelected = selectedCountry?.alpha2Code === country.alpha2Code
                      const code = country.callingCodes?.[0] || ""
                      
                      return (
                        <button
                          key={country.alpha2Code}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                            isSelected ? "bg-blue-50" : ""
                          }`}
                        >
                          <img
                            src={country.flag}
                            alt={country.name}
                            className="w-6 h-4 flex-shrink-0 rounded-sm object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700">
                              {country.name}
                            </div>
                            {code && (
                              <div className="text-xs text-gray-500">
                                +{code}
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 text-[#203a8a] flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Champ de saisie du numéro */}
        <input
          type="tel"
          name={name}
          value={displayValue}
          onChange={handlePhoneChange}
          placeholder={placeholder || "Numéro de téléphone"}
          disabled={disabled}
          required={required}
          className={`flex-1 px-3 py-2.5 border-2 rounded-r-lg focus:outline-none transition-all duration-300 ${
            error
              ? "border-red-300 focus:ring-2 focus:ring-red-500"
              : "border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a]"
          } ${
            disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          }`}
        />
      </div>
    </div>
  )
}
